import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { TimeRangeSelector, type TimeRange, type TimeRangeData } from '@/components/ui/time-range-selector';
import { Download, Info, MoreHorizontal } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { Toggle } from '@/components/ui/toggle';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ensureLocale,
  formatCurrency,
  formatCurrencyForExport,
  formatNumber,
  formatPercent,
  formatRatioForExport,
} from '@/lib/number-format';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  ReferenceArea,
  Tooltip as RechartsTooltip,
} from 'recharts';

type ValueType = 'currency' | 'number' | 'ratio' | 'percent';
type ChartFormatter = (value: number, locale: string) => string;

interface DataPoint {
  timestamp: string;
  value?: number;
  [key: string]: unknown;
}

interface ChartEvent {
  timestamp: string;
  type: 'start' | 'stop' | 'alert' | 'trade';
  label: string;
  color?: string;
}

interface ChartDataKey {
  key: string;
  label: string;
  color: string;
  valueType?: ValueType;
  axisFormatter?: ChartFormatter;
  tooltipFormatter?: ChartFormatter;
  exportFormatter?: ChartFormatter;
}

interface EnhancedChartOptions {
  variant?: 'default' | 'sparkline';
  showGrid?: boolean;
  yAxisLabel?: string;
  enableLogScale?: boolean;
  initialLogScale?: boolean;
  cumulativeKeys?: string[];
  enableDrawdown?: boolean;
  drawdownKey?: string;
  showSeriesToggle?: boolean;
  heightVariant?: 'compact' | 'standard' | 'tall';
}

interface EnhancedChartProps {
  title: string;
  data: DataPoint[];
  dataKeys: ChartDataKey[];
  events?: ChartEvent[];
  description?: string;
  allowExport?: boolean;
  timeRange?: TimeRange;
  onTimeRangeChange?: (range: TimeRangeData) => void;
  locale?: string;
  className?: string;
  loading?: boolean;
  emptyMessage?: string;
  emptyDescription?: string;
  customRange?: { from?: Date; to?: Date };
  refreshing?: boolean;
  options?: EnhancedChartOptions;
  headerActions?: React.ReactNode;
}

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const currencyFormatter = (decimals: number): ChartFormatter => (value, locale) =>
  formatCurrency(value, { locale, decimals, sign: 'never' });

const numberFormatter = (decimals: number): ChartFormatter => (value, locale) =>
  formatNumber(value, { locale, decimals, sign: 'never' });

const percentFormatter = (decimals: number): ChartFormatter => (value, locale) =>
  formatPercent(value, { locale, decimals, sign: 'never' });

const DEFAULT_FORMATTERS: Record<ValueType, Record<'axis' | 'tooltip' | 'export', ChartFormatter>> = {
  currency: {
    axis: currencyFormatter(0),
    tooltip: currencyFormatter(2),
    export: (value, locale) => formatCurrencyForExport(value, locale),
  },
  number: {
    axis: numberFormatter(0),
    tooltip: numberFormatter(2),
    export: numberFormatter(0),
  },
  ratio: {
    axis: numberFormatter(2),
    tooltip: numberFormatter(4),
    export: (value, locale) => formatRatioForExport(value, locale),
  },
  percent: {
    axis: percentFormatter(1),
    tooltip: percentFormatter(2),
    export: percentFormatter(2),
  },
};

const sanitizeExportValue = (value: string) => value.replace(/\u202F/g, ' ');

const resolveFormatter = (
  key: ChartDataKey | undefined,
  usage: 'axis' | 'tooltip' | 'export',
): ChartFormatter => {
  if (!key) {
    return DEFAULT_FORMATTERS.number[usage];
  }

  if (usage === 'axis' && key.axisFormatter) return key.axisFormatter;
  if (usage === 'tooltip' && key.tooltipFormatter) return key.tooltipFormatter;
  if (usage === 'export' && key.exportFormatter) return key.exportFormatter;

  const valueType = key.valueType ?? 'number';
  return DEFAULT_FORMATTERS[valueType][usage];
};

const formatNumericValue = (value: unknown, locale: string, formatter: ChartFormatter) => {
  if (!isFiniteNumber(value)) {
    return '—';
  }
  return formatter(value, locale);
};

const formatTimestamp = (formatter: Intl.DateTimeFormat, raw: string | number) => {
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) {
    return String(raw ?? '—');
  }
  return formatter.format(date);
};

export function EnhancedChart({
  title,
  data,
  dataKeys,
  events = [],
  description,
  allowExport = true,
  timeRange = '24h',
  onTimeRangeChange,
  locale,
  className,
  loading = false,
  emptyMessage = 'No trades in selected range.',
  emptyDescription = 'Adjust filters or timeframe to surface data.',
  customRange,
  refreshing = false,
  options,
  headerActions,
}: EnhancedChartProps) {
  const visibilityRef = useRef<HTMLDivElement | null>(null);
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(new Set());
  const [size, setSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const [logScale, setLogScale] = useState<boolean>(options?.initialLogScale ?? false);
  const [drawdownRange, setDrawdownRange] = useState<{ from: string; to: string } | null>(null);

  const effectiveLocale = ensureLocale(locale);

  const resolvedOptions = useMemo<EnhancedChartOptions>(() => ({
    variant: 'default',
    ...options,
  }), [options]);

  const variant = resolvedOptions.variant ?? 'default';
  const isSparkline = variant === 'sparkline';
  const showGrid = resolvedOptions.showGrid ?? !isSparkline;
  const showSeriesToggle = resolvedOptions.showSeriesToggle ?? dataKeys.length > 1;

  useEffect(() => {
    const node = visibilityRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, { threshold: 0.2 });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) {
      return;
    }
    const node = chartContainerRef.current;
    if (!node || typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setSize({ width, height });
    });

    observer.observe(node);
    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!resolvedOptions.enableLogScale) {
      setLogScale(false);
    }
  }, [resolvedOptions.enableLogScale]);

  useEffect(() => {
    setHiddenKeys(new Set());
  }, [dataKeys]);

  useEffect(() => {
    setDrawdownRange(null);
  }, [resolvedOptions.drawdownKey, resolvedOptions.enableDrawdown, data]);

  const hasDimensions = size.width >= 1 && size.height >= 1;
  const measuredChartWidth = Math.max(1, Math.floor(size.width));
  const measuredChartHeight = Math.max(1, Math.floor(size.height));

  const dataKeyLookup = useMemo(() => new Map(dataKeys.map((key) => [key.key, key])), [dataKeys]);

  const heightVariant: 'compact' | 'standard' | 'tall' =
    resolvedOptions.heightVariant ?? (isSparkline ? 'compact' : 'standard');

  const chartHeightClass =
    heightVariant === 'tall'
      ? 'h-[360px] lg:h-[400px]'
      : heightVariant === 'compact'
        ? 'h-[240px] lg:h-[260px]'
        : 'h-[300px] lg:h-[340px]';

  const cardClassName = cn('w-full', className);
  const chartMargin = useMemo(
    () => (isSparkline
      ? { top: 8, right: 12, left: resolvedOptions.yAxisLabel ? 32 : 12, bottom: 8 }
      : { top: 12, right: 16, left: resolvedOptions.yAxisLabel ? 40 : 12, bottom: 12 }),
    [isSparkline, resolvedOptions.yAxisLabel],
  );

  const axisNumberFormatter = useMemo(() => {
    const primaryKey = dataKeys[0];
    const formatter = resolveFormatter(primaryKey, 'axis');
    return (value: number) => formatNumericValue(value, effectiveLocale, formatter);
  }, [dataKeys, effectiveLocale]);

  const tooltipValueFormatter = useCallback(
    (dataKey: string, value: unknown) => {
      const formatter = resolveFormatter(dataKeyLookup.get(dataKey), 'tooltip');
      return formatNumericValue(value, effectiveLocale, formatter);
    },
    [dataKeyLookup, effectiveLocale],
  );

  const exportValueFormatter = useCallback(
    (dataKey: string, value: unknown) => {
      const formatter = resolveFormatter(dataKeyLookup.get(dataKey), 'export');
      if (!isFiniteNumber(value)) {
        return '';
      }
      return sanitizeExportValue(formatter(value, effectiveLocale));
    },
    [dataKeyLookup, effectiveLocale],
  );

  const xAxisFormatter = useMemo(
    () => new Intl.DateTimeFormat(effectiveLocale, { month: 'short', day: 'numeric' }),
    [effectiveLocale],
  );

  const tooltipTimestampFormatter = useMemo(
    () => new Intl.DateTimeFormat(effectiveLocale, { dateStyle: 'medium', timeStyle: 'short' }),
    [effectiveLocale],
  );

  const indexByTimestamp = useMemo(() => {
    const map = new Map<string, number>();
    data.forEach((point, index) => {
      map.set(String(point.timestamp), index);
    });
    return map;
  }, [data]);

  const cumulativeValues = useMemo(() => {
    if (!resolvedOptions.cumulativeKeys?.length) {
      return null;
    }
    const store = new Map<string, number[]>();
    resolvedOptions.cumulativeKeys.forEach((key) => {
      let total = 0;
      const totals: number[] = [];
      data.forEach((point) => {
        const value = Number(point[key] ?? 0);
        total += value;
        totals.push(total);
      });
      store.set(key, totals);
    });
    return store;
  }, [data, resolvedOptions.cumulativeKeys]);

  const refreshStateClass = refreshing && !loading ? 'pointer-events-none opacity-60 transition-opacity' : undefined;

  const handleLegendClick = useCallback(
    (dataKey: string) => {
      setHiddenKeys((prev) => {
        const next = new Set(prev);
        if (next.has(dataKey)) {
          next.delete(dataKey);
        } else {
          next.add(dataKey);
        }
        return next;
      });
    },
    [],
  );

  const handleExport = useCallback(() => {
    const headers = ['timestamp', ...dataKeys.map((key) => key.key)];
    const csvRows = data.map((row) =>
      headers.map((header) => {
        if (header === 'timestamp') {
          const raw = (row as Record<string, unknown>)[header];
          const date = new Date(String(raw ?? ''));
          return Number.isNaN(date.getTime()) ? String(raw ?? '') : date.toISOString();
        }
        const rawValue = (row as Record<string, unknown>)[header];
        return exportValueFormatter(header, rawValue);
      }),
    );

    const csvContent = [
      headers.join(','),
      ...csvRows.map((rowValues) =>
        rowValues
          .map((cell) => {
            const value = String(cell ?? '');
            if (value.includes(',')) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          })
          .join(','),
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${title.toLowerCase().replace(/\s+/g, '-')}-${timeRange}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  }, [data, dataKeys, exportValueFormatter, timeRange, title]);

  const handleExportPdf = useCallback(() => {
    const columns = ['Timestamp', ...dataKeys.map((key) => key.label)];
    const tableRows = data
      .map((row) => {
        const rawTimestamp = (row as Record<string, unknown>).timestamp;
        const normalizedTimestamp =
          typeof rawTimestamp === 'number' || typeof rawTimestamp === 'string'
            ? rawTimestamp
            : '';
        const timestampCell = formatTimestamp(tooltipTimestampFormatter, normalizedTimestamp);
        const valueCells = dataKeys.map((key) =>
          tooltipValueFormatter(
            key.key,
            (row as Record<string, unknown>)[key.key] ?? null,
          ),
        );
        return `<tr>${[timestampCell, ...valueCells]
          .map((cell) => `<td style="padding:6px 12px; border:1px solid #e2e8f0; font-size:12px;">${cell}</td>`)
          .join('')}</tr>`;
      })
      .join('');

    const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${title} — Export</title>
    <style>
      body { font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 24px; color: #0f172a; }
      h1 { font-size: 18px; margin-bottom: 12px; }
      table { border-collapse: collapse; width: 100%; }
      th { text-align: left; font-size: 12px; text-transform: uppercase; letter-spacing: 0.08em; background: #f8fafc; padding: 8px 12px; border: 1px solid #e2e8f0; }
    </style>
  </head>
  <body>
    <h1>${title} — ${timeRange.toUpperCase()} Snapshot</h1>
    <table>
      <thead>
        <tr>${columns
          .map((column) => `<th>${column}</th>`)
          .join('')}</tr>
      </thead>
      <tbody>
        ${tableRows}
      </tbody>
    </table>
  </body>
</html>`;

    const printWindow = window.open('', '', 'width=900,height=700');
    if (!printWindow) {
      return;
    }
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  }, [data, dataKeys, timeRange, title, tooltipTimestampFormatter, tooltipValueFormatter]);

  const drawdownKey = resolvedOptions.drawdownKey ?? dataKeys[0]?.key;
  const handleMouseMove = useCallback(
    (state: any) => {
      if (!resolvedOptions.enableDrawdown || !drawdownKey) {
        return;
      }
      const activeLabel = String(state?.activeLabel ?? '');
      if (!activeLabel || !indexByTimestamp.has(activeLabel)) {
        setDrawdownRange(null);
        return;
      }
      const idx = indexByTimestamp.get(activeLabel) ?? 0;
      if (idx <= 0) {
        setDrawdownRange(null);
        return;
      }
      let peakIndex = 0;
      let peakValue = Number(data[0]?.[drawdownKey] ?? 0);
      for (let i = 1; i <= idx; i += 1) {
        const candidate = Number(data[i]?.[drawdownKey] ?? 0);
        if (candidate >= peakValue) {
          peakValue = candidate;
          peakIndex = i;
        }
      }
      if (peakIndex === idx) {
        setDrawdownRange(null);
        return;
      }
      setDrawdownRange({
        from: String(data[peakIndex]?.timestamp ?? ''),
        to: String(data[idx]?.timestamp ?? ''),
      });
    },
    [resolvedOptions.enableDrawdown, drawdownKey, indexByTimestamp, data],
  );

  const renderTooltip = useCallback(
    ({ active, payload, label }: any) => {
      if (!active || !payload?.length) {
        return null;
      }

      const labelString = String(label ?? '');
      const pointIndex = indexByTimestamp.get(labelString) ?? -1;

      return (
        <div className="rounded-lg border border-slate-200 bg-white/95 p-3 shadow-lg backdrop-blur-sm">
          <p className="mb-2 text-sm font-medium text-slate-900">
            {formatTimestamp(tooltipTimestampFormatter, labelString)}
          </p>
          {payload.map((entry: any) => {
            const formatted = tooltipValueFormatter(entry.dataKey, entry.value);
            const cumulativeSeries = cumulativeValues?.get(entry.dataKey);
            const cumulativeValue =
              cumulativeSeries && pointIndex >= 0 ? cumulativeSeries[pointIndex] : undefined;
            return (
              <div key={entry.dataKey} className="text-xs font-medium" style={{ color: entry.color }}>
                <span>{entry.name}: {formatted}</span>
                {cumulativeValue !== undefined ? (
                  <span className="mt-1 block text-[11px] font-medium text-slate-500">
                    Cumulative: {tooltipValueFormatter(entry.dataKey, cumulativeValue)}
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
      );
    },
    [indexByTimestamp, tooltipTimestampFormatter, tooltipValueFormatter, cumulativeValues],
  );

  const renderSkeleton = (ariaBusy: boolean) => (
    <Card className={cardClassName} aria-busy={ariaBusy} aria-hidden={!ariaBusy}>
      <CardHeader className="p-grid-md w-full">
        <div className="flex items-center justify-between">
          <Skeleton className="h-5 w-40" />
          <div className="flex items-center gap-2">
            <Skeleton className="h-8 w-28" />
            {allowExport ? <Skeleton className="h-8 w-20" /> : null}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-grid-md w-full">
        <div className={`${chartHeightClass} relative rounded-xl border border-slate-200 bg-slate-50`}>
          <div className="absolute inset-x-6 bottom-6 flex gap-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={`chart-bar-${index}`} className="h-16 w-8 rounded-md" />
            ))}
          </div>
          <div className="absolute inset-x-6 top-6 flex h-[calc(100%-4rem)] flex-col justify-between">
            {Array.from({ length: 5 }).map((_, index) => (
              <Skeleton key={`chart-axis-${index}`} className="h-[2px] w-full" />
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (!isVisible) {
    return (
      <div ref={visibilityRef}>
        {renderSkeleton(false)}
      </div>
    );
  }

  if (loading) {
    return (
      <div ref={visibilityRef}>
        {renderSkeleton(true)}
      </div>
    );
  }

  return (
    <div ref={visibilityRef}>
      <Card className={cardClassName} aria-busy={refreshing}>
        <CardHeader className={cn('p-grid-md w-full', refreshStateClass)}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <CardTitle className="text-lg font-semibold text-slate-900">{title}</CardTitle>
              {description ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex h-6 w-6 items-center justify-center rounded-full text-slate-400 transition hover:text-slate-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-400"
                        aria-label={`More information about ${title}`}
                      >
                        <Info className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>{description}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {resolvedOptions.enableLogScale ? (
                <Toggle
                  pressed={logScale}
                  onPressedChange={setLogScale}
                  className="h-8 rounded-full border border-slate-200 bg-white px-3 text-xs font-semibold uppercase tracking-wide text-slate-600 data-[state=on]:bg-slate-900 data-[state=on]:text-white"
                  aria-label="Toggle logarithmic scale"
                >
                  Log
                </Toggle>
              ) : null}
              {headerActions}
              {allowExport ? (
                <div className="flex items-center gap-2">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={handleExport}
                          className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition hover:border-slate-300 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red/40 focus-visible:ring-offset-2"
                          aria-label="Export data"
                          data-testid="chart-export-button"
                        >
                          <Download className="h-4 w-4" aria-hidden="true" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>Export data</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition hover:border-slate-300 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red/40 focus-visible:ring-offset-2"
                        aria-label="Open export menu"
                      >
                        <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-40">
                      <DropdownMenuItem
                        onSelect={(event) => {
                          event.preventDefault();
                          handleExport();
                        }}
                      >
                        Export CSV
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={(event) => {
                          event.preventDefault();
                          handleExportPdf();
                        }}
                      >
                        Export PDF
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ) : null}
              {onTimeRangeChange ? (
                <TimeRangeSelector
                  value={timeRange}
                  onChange={onTimeRangeChange}
                  customRange={customRange}
                />
              ) : null}
            </div>
          </div>
        </CardHeader>
        <CardContent className={cn('p-grid-md w-full min-w-0', refreshStateClass)}>
          {data.length === 0 ? (
            <div className={`${chartHeightClass} flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500`}>
              <span>{emptyMessage}</span>
              {emptyDescription ? (
                <span className="mt-1 text-xs text-slate-400">{emptyDescription}</span>
              ) : null}
            </div>
          ) : (
            <>
              <div ref={chartContainerRef} className={`${chartHeightClass} w-full`} style={{ minWidth: 0, minHeight: 0 }}>
                {hasDimensions ? (
                  <ResponsiveContainer width={measuredChartWidth} height={measuredChartHeight}>
                    <LineChart
                      data={data}
                      margin={chartMargin}
                      onMouseMove={handleMouseMove}
                      onMouseLeave={() => setDrawdownRange(null)}
                    >
                      {showGrid ? <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" /> : null}
                      <XAxis
                        dataKey="timestamp"
                        tickFormatter={(value) => formatTimestamp(xAxisFormatter, value)}
                        stroke="#6b7280"
                        fontSize={isSparkline ? 11 : 12}
                        hide={isSparkline}
                      />
                      <YAxis
                        tickFormatter={axisNumberFormatter}
                        stroke="#6b7280"
                        fontSize={isSparkline ? 11 : 12}
                        tickLine={!isSparkline}
                        axisLine={!isSparkline}
                        label={resolvedOptions.yAxisLabel
                          ? {
                              value: resolvedOptions.yAxisLabel,
                              angle: -90,
                              position: 'insideLeft',
                              offset: -8,
                              fill: '#475569',
                              fontSize: 11,
                            }
                          : undefined}
                        scale={resolvedOptions.enableLogScale && logScale ? 'log' : undefined}
                        domain={resolvedOptions.enableLogScale && logScale ? ['auto', 'auto'] : undefined}
                        allowDecimals
                      />
                      <RechartsTooltip content={renderTooltip} />

                      {resolvedOptions.enableDrawdown && drawdownRange ? (
                        <ReferenceArea
                          x1={drawdownRange.from}
                          x2={drawdownRange.to}
                          fill="rgba(248, 113, 113, 0.18)"
                          stroke={undefined}
                        />
                      ) : null}

                      {events.map((event, index) => (
                        <ReferenceLine
                          key={`event-${event.timestamp}-${index}`}
                          x={event.timestamp}
                          stroke={event.color || '#ef4444'}
                          strokeDasharray="2 2"
                          label={{ value: event.label, position: 'top' }}
                        />
                      ))}

                      {dataKeys.map((dataKey) => (
                        <Line
                          key={dataKey.key}
                          type="monotone"
                          dataKey={dataKey.key}
                          stroke={dataKey.color}
                          strokeWidth={isSparkline ? 2 : 2}
                          dot={false}
                          activeDot={{ r: 3 }}
                          hide={hiddenKeys.has(dataKey.key)}
                          name={dataKey.label}
                          isAnimationActive={false}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-xs text-slate-400">
                    Preparing chart…
                  </div>
                )}
              </div>

              {showSeriesToggle ? (
                <div className="mt-4 flex flex-wrap gap-2">
                  {dataKeys.map((dataKey) => {
                    const isHidden = hiddenKeys.has(dataKey.key);
                    return (
                      <button
                        key={dataKey.key}
                        type="button"
                        className={cn(
                          'flex items-center gap-2 rounded-full border px-3 py-1 text-xs transition-colors',
                          isHidden
                            ? 'border-slate-200 bg-white text-slate-500'
                            : 'border-transparent bg-slate-100 text-slate-700',
                        )}
                        onClick={() => handleLegendClick(dataKey.key)}
                        aria-pressed={!isHidden}
                        aria-label={`${isHidden ? 'Show' : 'Hide'} ${dataKey.label}`}
                        data-testid={`legend-${dataKey.key}`}
                      >
                        <span
                          className="h-2.5 w-2.5 rounded-full"
                          style={{ backgroundColor: dataKey.color }}
                          aria-hidden="true"
                        />
                        {dataKey.label}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
